import { useState, useEffect, useCallback, useRef } from 'react';
import { useAuthStore } from '@/store/authStore';
import { agendaService } from '@/services/agendaService';
import { Appointment } from '@/services/appointmentService';

const POLL_INTERVAL_MS = 5 * 60 * 1000;

const ACTIVE_STATUSES = new Set(['SCHEDULED', 'CONFIRMED', 'IN_PROGRESS']);

export function useMyTodayAppointments() {
  const user = useAuthStore((s) => s.user);
  const [appointments, setAppointments] = useState<Appointment[]>([]);
  const [loading, setLoading] = useState(true);
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const fetch = useCallback(async () => {
    if (!user?.id) return;
    try {
      const res = await agendaService.getAgendaToday(user.id);
      const list: Appointment[] = res.data?.appointments ?? [];
      list.sort(
        (a, b) =>
          new Date(a.startTime).getTime() - new Date(b.startTime).getTime(),
      );
      setAppointments(list);
    } catch (err) {
      console.error('useMyTodayAppointments: fetch failed', err);
    } finally {
      setLoading(false);
    }
  }, [user?.id]);

  useEffect(() => {
    fetch();
    intervalRef.current = setInterval(fetch, POLL_INTERVAL_MS);
    return () => {
      if (intervalRef.current) clearInterval(intervalRef.current);
    };
  }, [fetch]);

  const activeCount = appointments.filter((a) =>
    ACTIVE_STATUSES.has(a.status),
  ).length;

  return { appointments, activeCount, loading, refetch: fetch };
}
